import React from "react";
import {
  Box, TextField, MenuItem, Autocomplete,
  IconButton, Tooltip, useTheme,
} from "@mui/material";
import { useState } from "react";
import { useSelector } from "react-redux";
import { requester } from "api/pesabackend";
import { CancelOutlined } from "@mui/icons-material";
import TrendingUpIcon   from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import RemoveIcon       from "@mui/icons-material/Remove";

const PurchaseDraftDetailForm = React.memo((props) => {
  const {
    index, data,
    handleRemoveProductDetailRow,
    handleItemAmountChange,
    name, errors, touched,
    handleBlur, handleChange, setFieldValue,
  } = props;

  const theme  = useTheme();
  const isDark = theme.palette.mode === "dark";

  const textSec = theme.palette.neutral.main;
  const border  = isDark ? "rgba(29,158,117,0.14)" : "rgba(29,158,117,0.16)";
  const bodyBg  = theme.palette.background.alt;
  const cardBg  = isDark ? "rgba(255,255,255,0.03)" : "#ffffff";

  const [productList,           setProductList]           = useState(data.product_info ? [data.product_info] : []);
  const [unitsForGivenItemList, setUnitsForGivenItemList] = useState(data.product_units_list ?? []);
  const [unitCost,              setUnitCost]              = useState(0);
  const [selectedUnitId,        setSelectedUnitId]        = useState(data.related_product_unit || "");

  const business_branch_id = useSelector((s) => s.authReducer.business_branch_id);

  /* ── Last purchase price from selected unit ── */
  const selectedUnit          = unitsForGivenItemList.find((u) => u.id === selectedUnitId);
  const lastPurchaseUnitPrice = selectedUnit ? parseFloat(selectedUnit.last_purchase_unit_price || 0) : null;

  const priceCompare =
    lastPurchaseUnitPrice === null || unitCost === 0 ? null :
    parseFloat(unitCost) < lastPurchaseUnitPrice ? "below" :
    parseFloat(unitCost) > lastPurchaseUnitPrice ? "above" : "equal";

  const compareColor =
    priceCompare === "below" ? "#1D9E75" :
    priceCompare === "above" ? "#e24b4a" : textSec;

  const CompareIcon =
    priceCompare === "below" ? TrendingDownIcon :
    priceCompare === "above" ? TrendingUpIcon   : RemoveIcon;

  const fetchUnitsForGivenItemList = async (productId) => {
    try {
      const res = await requester.get(`/inventory/products/unit_list/${productId}`);
      setUnitsForGivenItemList(res.data.data);
    } catch {}
  };

  const handleUnitCostChange = (quantity, amount) => {
    const cost = quantity > 0 ? (Number(amount) / Number(quantity)).toFixed(2) : 0;
    setUnitCost(cost);
  };

  const fetchItemList = async (search) => {
    try {
      const url = search
        ? `/inventory/products/list?business_branch=${business_branch_id}&search=${search}&typeFilter=all`
        : `/inventory/products/list?business_branch=${business_branch_id}&typeFilter=all`;
      const res = await requester.get(url);
      setProductList(res.data.data);
    } catch {}
  };

  const errP = (field) =>
    Boolean(touched?.products_purchased?.[index]?.[field]) &&
    Boolean(errors?.products_purchased?.[index]?.[field]);

  /* ── Shared compact field sx ── */
  const fSx = {
    "& .MuiInputBase-root": {
      fontSize: "12px", height: "28px",
      backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "#fafafa",
      borderRadius: "6px",
    },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: border },
    "& .MuiInputLabel-root": { fontSize: "10px" },
    "& .MuiSelect-select": { fontSize: "12px", py: "4px" },
    "& .MuiAutocomplete-input": { fontSize: "12px", py: "2px !important" },
  };

  /* ── Disabled field sx — slightly dimmed bg, no pointer ── */
  const fSxDisabled = {
    ...fSx,
    "& .MuiInputBase-root": {
      ...fSx["& .MuiInputBase-root"],
      backgroundColor: isDark ? "rgba(255,255,255,0.015)" : "#f0f0f0",
      cursor: "not-allowed",
    },
    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: priceCompare ? compareColor : (isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)"),
      fontWeight: priceCompare ? 600 : 400,
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: priceCompare ? compareColor : border,
    },
  };

  return (
    <Box sx={{
      display: "flex",
      alignItems: "center",
      gap: "6px",
      px: "10px", paddingBottom: "6px", paddingTop:"14px",
      backgroundColor: cardBg,
      border: `0.5px solid ${border}`,
      borderRadius: "8px",
    }}>

      {/* ── Index badge ── */}
      <Box sx={{
        fontSize: "10px", fontWeight: 700, letterSpacing: "0.05em",
        color: textSec, backgroundColor: bodyBg,
        border: `0.5px solid ${border}`, borderRadius: "4px",
        px: "5px", py: "3px", flexShrink: 0,
      }}>
        #{index + 1}
      </Box>

      {/* 1. Product name */}
      <Box sx={{ flex: "2 1 120px", minWidth: 0 }}>
        <Autocomplete
          size="small"
          onOpen={() => { fetchItemList(null); }}
          onChange={(e, value) => {
            if (value) { fetchUnitsForGivenItemList(value.id); setSelectedUnitId(""); }
            setFieldValue(`${name}.related_product`, value ? value.id : "");
            setFieldValue(`${name}.related_product_unit`, "");
          }}
          options={productList}
          defaultValue={productList[0]}
          getOptionLabel={(o) => `${o.product_name} ${o.description}`}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          renderOption={(props, item) => (
            <Box component="li" {...props} key={item.id} sx={{ fontSize: "12px" }}>
              {item.product_name} {item.description}
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Item Name"
              error={errP("related_product")}
              onBlur={handleBlur}
              onChange={(e) => { fetchItemList(e.currentTarget.value); }}
              sx={fSx}
            />
          )}
        />
      </Box>

      {/* 2. Item Unit */}
      <Box sx={{ flex: "1 1 90px", minWidth: 0 }}>
        <TextField
          select size="small" fullWidth label="Item Unit"
          onChange={(e) => {
            handleChange(e);
            setSelectedUnitId(e.target.value);
          }}
          value={data.related_product_unit}
          name={`${name}.related_product_unit`}
          error={errP("related_product_unit")}
          sx={fSx}
        >
          {unitsForGivenItemList.map((u, i) => (
            <MenuItem key={i} value={u.id} sx={{ fontSize: "12px" }}>
              {u.related_business_product_unit.name}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {/* 3. Batch */}
      <Box sx={{ flex: "1 1 80px", minWidth: 0 }}>
        <TextField
          size="small" fullWidth label="Batch"
          onChange={handleChange}
          value={data.batch}
          name={`${name}.batch`}
          error={errP("batch")}
          sx={fSx}
        />
      </Box>

      {/* 4. Expiry Date */}
      <Box sx={{ flex: "1 1 90px", minWidth: 0 }}>
        <TextField
          size="small" fullWidth type="date"
          label="Expiry Date"
          onChange={handleChange}
          value={data.expiry_date}
          name={`${name}.expiry_date`}
          error={errP("expiry_date")}
          InputLabelProps={{ shrink: true }}
          sx={fSx}
        />
      </Box>

      {/* 5. Quantity */}
      <Box sx={{ flex: "0 0 100px", minWidth: 0 }}>
        <TextField
          size="small" fullWidth type="number" label="Quantity"
          inputProps={{ min: 0, style: { fontSize: "12px" } }}
          onChange={(e) => {
            handleChange(e);
            handleUnitCostChange(e.target.value, data.amount);
          }}
          value={data.quantity_purchased}
          name={`${name}.quantity_purchased`}
          error={errP("quantity_purchased")}
          sx={fSx}
        />
      </Box>

      {/* 6. Last Unit Cost — disabled, colored by comparison */}
      <Box sx={{ flex: "0 0 100px", minWidth: 0 }}>
        <TextField
          size="small" fullWidth disabled
          label="Last Unit Cost"
          value={lastPurchaseUnitPrice !== null ? Number(lastPurchaseUnitPrice).toFixed(2) : "—"}
          InputProps={{
            startAdornment: priceCompare ? (
              <CompareIcon sx={{ fontSize: "12px", color: compareColor, mr: "3px", flexShrink: 0 }} />
            ) : null,
          }}
          sx={fSxDisabled}
        />
      </Box>

      {/* 7. Unit Cost — disabled, computed from amount / qty */}
      <Box sx={{ flex: "0 0 100px", minWidth: 0 }}>
        <input
          type="hidden"
          name={`${name}.unit_cost`}
          value={(data.unit_cost = unitCost)}
          onChange={handleChange}
        />
        <TextField
          size="small" fullWidth disabled
          label="Unit Cost"
          value={Number(unitCost).toFixed(2)}
          sx={fSx}
        />
      </Box>

      {/* 8. Amount */}
      <Box sx={{ flex: "0 0 100px", minWidth: 0 }}>
        <TextField
          size="small" fullWidth type="number" label="Amount"
          inputProps={{ min: 0, style: { fontSize: "12px" } }}
          onChange={(e) => {
            handleChange(e);
            handleItemAmountChange(e, index);
            handleUnitCostChange(data.quantity_purchased, e.target.value);
          }}
          value={data.amount}
          name={`${name}.amount`}
          error={errP("amount")}
          sx={fSx}
        />
      </Box>

      {/* Remove */}
      {index > 0 && (
        <Tooltip title="Remove row">
          <IconButton
            onClick={() => handleRemoveProductDetailRow(index)}
            size="small"
            sx={{
              flexShrink: 0,
              color: "#e24b4a",
              border: "0.5px solid rgba(226,75,74,0.25)",
              borderRadius: "5px",
              width: "24px", height: "24px",
              "&:hover": { backgroundColor: "rgba(226,75,74,0.08)" },
            }}
          >
            <CancelOutlined sx={{ fontSize: "14px" }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
});

export default PurchaseDraftDetailForm;